import { pageService } from './page.service';
import { companyService } from './company.service';

export class DataLoaderService {
  private loadingPromises: Map<string, Promise<any>> = new Map();
  private preloadedData: Map<string, any> = new Map();

  /**
   * Preload data for multiple routes
   */
  async preloadData(routes: string[]) {
    const promises = routes.map(route => {
      switch (route) {
        case '/':
        case '/home':
          return this.loadHomeData();
        case '/servicios':
          return this.loadServicesData();
        case '/contacto':
          return this.loadContactData();
        case '/quienes-somos':
          return this.loadAboutData();
        case '/blog':
          return this.loadBlogData();
        default:
          // For dynamic routes or unknown routes
          return this.loadGenericPageData(route);
      }
    });

    await Promise.allSettled(promises); // Use allSettled to not fail if one route fails
  }

  /**
   * Load home page data with related content
   */
  async loadHomeData() {
    const key = 'home-data';
    
    if (!this.loadingPromises.has(key)) {
      const promise = this.loadHomeDataInternal();
      this.loadingPromises.set(key, promise);
    }
    
    return this.loadingPromises.get(key);
  }

  /**
   * Load services page data
   */
  async loadServicesData() {
    const key = 'services-data';
    
    if (!this.loadingPromises.has(key)) {
      const promise = this.loadPageBySlug('servicios');
      this.loadingPromises.set(key, promise);
    }
    
    return this.loadingPromises.get(key);
  }

  /**
   * Load contact page data
   */
  async loadContactData() {
    const key = 'contact-data';
    
    if (!this.loadingPromises.has(key)) {
      const promise = this.loadPageBySlug('contacto');
      this.loadingPromises.set(key, promise);
    }
    
    return this.loadingPromises.get(key);
  }

  /**
   * Load about page data
   */
  async loadAboutData() {
    const key = 'about-data';
    
    if (!this.loadingPromises.has(key)) {
      const promise = this.loadPageBySlug('quienes-somos');
      this.loadingPromises.set(key, promise);
    }
    
    return this.loadingPromises.get(key);
  }

  /**
   * Load blog page data
   */
  async loadBlogData() {
    const key = 'blog-data';
    
    if (!this.loadingPromises.has(key)) {
      const promise = this.loadPageBySlug('blog');
      this.loadingPromises.set(key, promise);
    }
    
    return this.loadingPromises.get(key);
  }

  /**
   * Load any page by slug
   */
  async loadGenericPageData(route: string) {
    // Extract slug from route (remove leading slash and trailing params)
    const slug = route.replace(/^\//, '').split('?')[0].split('#')[0];
    
    if (!slug) return Promise.resolve();

    const key = `page-${slug}`;
    
    if (!this.loadingPromises.has(key)) {
      const promise = this.loadPageBySlug(slug);
      this.loadingPromises.set(key, promise);
    }
    
    return this.loadingPromises.get(key);
  }

  /**
   * Internal method to load home data with related content
   */
  private async loadHomeDataInternal() {
    try {
      // Load home page and related data in parallel
      const [homePage, propertiesData, blogData] = await Promise.allSettled([
        pageService.getHomePage(),
        this.loadFeaturedProperties(),
        this.loadFeaturedBlogPosts()
      ]);

      const result = {
        page: homePage.status === 'fulfilled' ? homePage.value : null,
        properties: propertiesData.status === 'fulfilled' ? propertiesData.value : [],
        blog: blogData.status === 'fulfilled' ? blogData.value : [],
        timestamp: Date.now()
      };

      // Cache the result
      this.preloadedData.set('home-data', result);
      
      return result;
    } catch (error) {
      console.error('Error preloading home data:', error);
      
      // Return empty result but don't throw to avoid breaking the app
      const fallbackResult = {
        page: null,
        properties: [],
        blog: [],
        timestamp: Date.now(),
        error: error.message
      };
      
      this.preloadedData.set('home-data', fallbackResult);
      return fallbackResult;
    }
  }

  /**
   * Load page by slug
   */
  private async loadPageBySlug(slug: string) {
    try {
      const page = await pageService.getPageBySlug(slug, true);
      
      const result = {
        page,
        timestamp: Date.now()
      };

      this.preloadedData.set(`page-${slug}`, result);
      return result;
    } catch (error) {
      console.error(`Error preloading page ${slug}:`, error);
      
      const fallbackResult = {
        page: null,
        timestamp: Date.now(),
        error: error.message
      };
      
      this.preloadedData.set(`page-${slug}`, fallbackResult);
      return fallbackResult;
    }
  }

  /**
   * Load featured properties (if available)
   */
  private async loadFeaturedProperties() {
    try {
      // This would use your properties API
      const response = await fetch('/api/v1/properties/featured');
      if (response.ok) {
        return await response.json();
      }
      return [];
    } catch (error) {
      console.warn('Could not preload featured properties:', error);
      return [];
    }
  }

  /**
   * Load featured blog posts (if available)
   */
  private async loadFeaturedBlogPosts() {
    try {
      // This would use your blog API
      const response = await fetch('/api/v1/blog/featured');
      if (response.ok) {
        return await response.json();
      }
      return [];
    } catch (error) {
      console.warn('Could not preload featured blog posts:', error);
      return [];
    }
  }

  /**
   * Preload tenant configuration
   */
  async preloadTenantConfig(slug?: string) {
    const key = 'tenant-config';
    
    if (!this.loadingPromises.has(key)) {
      const promise = this.loadTenantConfigInternal(slug);
      this.loadingPromises.set(key, promise);
    }
    
    return this.loadingPromises.get(key);
  }

  /**
   * Internal method to load tenant configuration
   */
  private async loadTenantConfigInternal(slug?: string) {
    try {
      const tenantSlug = slug || companyService.getDefaultCompanySlug();
      const config = await companyService.getCompanyBySlug(tenantSlug);
      
      const result = {
        config,
        timestamp: Date.now()
      };

      this.preloadedData.set('tenant-config', result);
      return result;
    } catch (error) {
      console.error('Error preloading tenant config:', error);
      
      const fallbackResult = {
        config: null,
        timestamp: Date.now(),
        error: error.message
      };
      
      this.preloadedData.set('tenant-config', fallbackResult);
      return fallbackResult;
    }
  }

  /**
   * Get preloaded data if available
   */
  getPreloadedData(key: string) {
    return this.preloadedData.get(key);
  }

  /**
   * Check if data has been preloaded
   */
  isDataPreloaded(key: string): boolean {
    return this.preloadedData.has(key);
  }

  /**
   * Clear preloaded data (useful for cache management)
   */
  clearPreloadedData(key?: string) {
    if (key) {
      this.preloadedData.delete(key);
      this.loadingPromises.delete(key);
    } else {
      this.preloadedData.clear();
      this.loadingPromises.clear();
    }
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return {
      preloadedItems: this.preloadedData.size,
      activePromises: this.loadingPromises.size,
      keys: Array.from(this.preloadedData.keys())
    };
  }

  /**
   * Warm up cache for critical data
   */
  async warmupCache(tenantSlug?: string) {
    const criticalRoutes = ['/', '/servicios', '/contacto'];
    
    try {
      // Preload tenant config first
      await this.preloadTenantConfig(tenantSlug);
      
      // Then preload critical pages
      await this.preloadData(criticalRoutes);
      
      console.log('Cache warmed up successfully', this.getCacheStats());
    } catch (error) {
      console.error('Error warming up cache:', error);
    }
  }

  /**
   * Prefetch data for route navigation
   */
  async prefetchRoute(route: string) {
    // Don't wait for the result, just start the loading process
    this.preloadData([route]).catch(error => {
      console.warn(`Failed to prefetch route ${route}:`, error);
    });
  }

  /**
   * Clean expired cache entries (older than 5 minutes)
   */
  cleanExpiredCache() {
    const now = Date.now();
    const maxAge = 5 * 60 * 1000; // 5 minutes

    for (const [key, data] of this.preloadedData.entries()) {
      if (data.timestamp && (now - data.timestamp) > maxAge) {
        this.preloadedData.delete(key);
        this.loadingPromises.delete(key);
      }
    }
  }
}

export const dataLoader = new DataLoaderService();

// Clean expired cache every 5 minutes
if (typeof window !== 'undefined') {
  setInterval(() => {
    dataLoader.cleanExpiredCache();
  }, 5 * 60 * 1000);
}